import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Shell from '../../components/Shell';
import api from '../../services/api';

export type ContactPhase =
  | 'to_contact'
  | 'reached_out'
  | 'invited'
  | 'in_conversation'
  | 'soft_interest'
  | 'committed'
  | 'passed';

export type Contact = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  notes?: string;
  phase: ContactPhase;
  visibleFundSlugs?: string[];
  entitlements?: string[];
  userId?: string | null;
  tags?: string[];
  updatedAt?: string;
};

type PhaseMeta = { id: ContactPhase; label: string };

type UserHit = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles?: string[];
  active?: boolean;
};

const PHASE_FALLBACK: PhaseMeta[] = [
  { id: 'to_contact', label: 'To contact' },
  { id: 'reached_out', label: 'Reached out' },
  { id: 'invited', label: 'Invited' },
  { id: 'in_conversation', label: 'In conversation' },
  { id: 'soft_interest', label: 'Soft interest' },
  { id: 'committed', label: 'Committed' },
  { id: 'passed', label: 'Passed' },
];

export default function AdminCrmPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [phases, setPhases] = useState<PhaseMeta[]>(PHASE_FALLBACK);
  const [msg, setMsg] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const didDrag = useRef(false);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [userHits, setUserHits] = useState<UserHit[]>([]);
  const [contactHits, setContactHits] = useState<Contact[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
  });

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([
      api.get('/admin/crm/contacts'),
      api.get('/admin/crm/phases'),
    ]);
    setContacts(c.data);
    if (Array.isArray(p.data) && p.data.length) setPhases(p.data);
  }, []);

  useEffect(() => {
    load().catch(() => setMsg('Failed to load CRM'));
  }, [load]);

  useEffect(() => {
    if (!showAdd) return;
    const q = query.trim();
    if (q.length < 1) {
      setUserHits([]);
      setContactHits([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      api
        .get('/admin/crm/search', { params: { q } })
        .then((r) => {
          if (cancelled) return;
          setContactHits(r.data.contacts || []);
          setUserHits(r.data.users || []);
        })
        .catch(() => {
          if (!cancelled) setMsg('Search failed');
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, showAdd]);

  const byPhase = useMemo(() => {
    const map: Record<string, Contact[]> = {};
    for (const p of phases) map[p.id] = [];
    for (const c of contacts) {
      const key = map[c.phase] ? c.phase : 'to_contact';
      map[key].push(c);
    }
    return map;
  }, [contacts, phases]);

  function resetAdd() {
    setQuery('');
    setUserHits([]);
    setContactHits([]);
    setShowCreateForm(false);
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
    });
  }

  async function addExistingUser(user: UserHit) {
    setMsg('');
    try {
      const { data } = await api.post('/admin/crm/contacts/from-user', {
        userId: user._id,
      });
      resetAdd();
      setShowAdd(false);
      setMsg(`Added ${user.firstName} ${user.lastName} to the pipeline.`);
      await load();
      navigate(`/admin/crm/${data._id}`);
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Could not add user');
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    try {
      const { data } = await api.post('/admin/crm/contacts', form);
      resetAdd();
      setShowAdd(false);
      setMsg('Contact added.');
      await load();
      navigate(`/admin/crm/${data._id}`);
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Create failed');
    }
  }

  async function moveToPhase(contactId: string, phase: ContactPhase) {
    setContacts((prev) =>
      prev.map((c) => (c._id === contactId ? { ...c, phase } : c)),
    );
    try {
      await api.patch(`/admin/crm/contacts/${contactId}/phase`, { phase });
    } catch {
      setMsg('Failed to update phase');
      await load();
    }
  }

  const q = query.trim();
  const noHits =
    q.length > 0 && !searching && userHits.length === 0 && contactHits.length === 0;

  return (
    <Shell title="Admin · CRM" wide>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Partnership pipeline</h1>
          <p className="mt-1 text-sm text-ink-700">
            Drag contacts between phases. Open a card for visibility and email.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setShowAdd((v) => !v);
            if (showAdd) resetAdd();
          }}
          className="bg-navy-900 px-4 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700"
        >
          {showAdd ? 'Cancel' : 'Add'}
        </button>
      </div>

      {showAdd && (
        <div className="mt-6 max-w-3xl border border-mist-200 bg-white p-5">
          <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-stone-600">
            Search people
          </label>
          <input
            autoFocus
            className="mt-2 w-full"
            placeholder="Name or email — existing portal users first"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowCreateForm(false);
            }}
          />
          <p className="mt-2 text-xs text-ink-700">
            Pick an existing user to add them to the pipeline, or create a new
            contact if they are not on the platform yet.
          </p>

          {searching && (
            <p className="mt-3 text-sm text-ink-700">Searching…</p>
          )}

          {contactHits.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-600">
                Already in CRM
              </p>
              <ul className="mt-2 divide-y divide-mist-200 border border-mist-200">
                {contactHits.map((c) => (
                  <li key={c._id}>
                    <button
                      type="button"
                      onClick={() => {
                        resetAdd();
                        setShowAdd(false);
                        navigate(`/admin/crm/${c._id}`);
                      }}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-mist-50"
                    >
                      <span>
                        <span className="block text-sm font-medium">
                          {c.firstName} {c.lastName}
                        </span>
                        <span className="text-xs text-ink-700">{c.email}</span>
                      </span>
                      <span className="text-xs text-navy-700">Open</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {userHits.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-600">
                Portal users
              </p>
              <ul className="mt-2 divide-y divide-mist-200 border border-mist-200">
                {userHits.map((u) => (
                  <li key={u._id}>
                    <button
                      type="button"
                      onClick={() => addExistingUser(u)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-mist-50"
                    >
                      <span>
                        <span className="block text-sm font-medium">
                          {u.firstName} {u.lastName}
                        </span>
                        <span className="text-xs text-ink-700">{u.email}</span>
                      </span>
                      <span className="text-xs text-navy-700">Add to CRM</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(noHits || showCreateForm) && (
            <div className="mt-5 border-t border-mist-200 pt-4">
              {!showCreateForm ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(true);
                    const parts = q.split(/\s+/);
                    const maybeEmail = q.includes('@') ? q : '';
                    setForm((f) => ({
                      ...f,
                      email: maybeEmail || f.email,
                      firstName:
                        !maybeEmail && parts[0] ? parts[0] : f.firstName,
                      lastName:
                        !maybeEmail && parts.length > 1
                          ? parts.slice(1).join(' ')
                          : f.lastName,
                    }));
                  }}
                  className="text-sm font-semibold text-navy-700 underline"
                >
                  No match — create new contact
                  {q ? ` for “${q}”` : ''}
                </button>
              ) : (
                <form
                  onSubmit={onCreate}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  <p className="sm:col-span-2 text-sm text-ink-700">
                    Creating a new CRM contact (not a portal login until you
                    invite them).
                  </p>
                  <input
                    required
                    placeholder="First name"
                    value={form.firstName}
                    onChange={(e) =>
                      setForm({ ...form, firstName: e.target.value })
                    }
                  />
                  <input
                    required
                    placeholder="Last name"
                    value={form.lastName}
                    onChange={(e) =>
                      setForm({ ...form, lastName: e.target.value })
                    }
                  />
                  <input
                    required
                    type="email"
                    className="sm:col-span-2"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                  />
                  <input
                    placeholder="Phone"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                  <input
                    placeholder="Company / affiliation"
                    value={form.company}
                    onChange={(e) =>
                      setForm({ ...form, company: e.target.value })
                    }
                  />
                  <textarea
                    className="sm:col-span-2 min-h-[80px]"
                    placeholder="Notes"
                    value={form.notes}
                    onChange={(e) =>
                      setForm({ ...form, notes: e.target.value })
                    }
                  />
                  <button
                    type="submit"
                    className="sm:col-span-2 bg-navy-900 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700"
                  >
                    Create contact
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}

      {msg && <p className="mt-3 text-sm text-ink-700">{msg}</p>}

      <div className="mt-8 flex gap-3 overflow-x-auto pb-4">
        {phases.map((phase) => (
          <div
            key={phase.id}
            className="flex w-56 shrink-0 flex-col bg-mist-100/80"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const id =
                e.dataTransfer.getData('text/contact-id') || draggingId;
              if (id) moveToPhase(id, phase.id);
              setDraggingId(null);
            }}
          >
            <div className="border-b border-mist-200 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-600">
                {phase.label}
              </p>
              <p className="text-xs text-ink-700/70">
                {(byPhase[phase.id] || []).length}
              </p>
            </div>
            <ul className="flex min-h-[12rem] flex-col gap-2 p-2">
              {(byPhase[phase.id] || []).map((c) => (
                <li key={c._id}>
                  <button
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      didDrag.current = true;
                      setDraggingId(c._id);
                      e.dataTransfer.setData('text/contact-id', c._id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setTimeout(() => {
                        didDrag.current = false;
                      }, 0);
                    }}
                    onClick={() => {
                      if (didDrag.current) return;
                      navigate(`/admin/crm/${c._id}`);
                    }}
                    className={`block w-full cursor-grab border border-mist-200 bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-navy-400 active:cursor-grabbing ${
                      draggingId === c._id ? 'opacity-50' : ''
                    }`}
                  >
                    <p className="text-sm font-medium text-ink-950">
                      {c.firstName} {c.lastName}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-ink-700">
                      {c.email}
                    </p>
                    {c.company && (
                      <p className="mt-0.5 truncate text-xs text-stone-500">
                        {c.company}
                      </p>
                    )}
                    {c.userId && (
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-navy-700">
                        Portal linked
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Shell>
  );
}
